import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Fingerprint, Megaphone } from 'lucide-react';

import { AuthLayout } from '../components/AuthLayout';
import { CompanySelectStep } from '../components/CompanySelectStep';
import { Seo } from '../components/Seo';
import { WhatsAppIcon, WHATSAPP_URL } from '../components/WhatsAppIcon';
import { Button, FormError, Spinner, TextField } from '../components/ui';
import { ApiError } from '../lib/api';
import { trackCta } from '../lib/analytics';
import { IS_NATIVE } from '../lib/base';
import { hasBiometricToken, isBiometricAvailable } from '../lib/native/biometricAuth';
import { useAnnouncements } from '../lib/announcements';
import { useAuthConfig } from '../lib/auth/authConfig';
import { executeRecaptcha, preloadRecaptcha } from '../lib/auth/recaptcha';
import { useAuth } from '../lib/auth/AuthProvider';
import { formatRelative } from '../lib/format';
import { getLocale, t } from '../lib/i18n';

export function LoginPage() {
  const { login, loginWithBiometrics, pendingCompanies, selectCompany, cancelSelection } = useAuth();
  const authConfig = useAuthConfig();
  const recaptchaSiteKey = authConfig.data?.recaptcha_site_key ?? '';
  // Precargamos el reCAPTCHA al abrir el login para que el token esté listo al
  // enviar (sin esperar la carga en el submit).
  useEffect(() => {
    if (!IS_NATIVE) preloadRecaptcha(recaptchaSiteKey || undefined);
  }, [recaptchaSiteKey]);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // `next` (query) tiene prioridad sobre el `from` guardado en state: lo usa el
  // flujo de invitación para volver al link tras loguearse. `reason=invite`
  // muestra el aviso "ingresá para aceptar la invitación"; `email` prellena.
  const nextParam = searchParams.get('next');
  const emailParam = searchParams.get('email') ?? '';
  const isInviteReason = searchParams.get('reason') === 'invite';
  const from = nextParam || (location.state as { from?: string } | null)?.from || '/inicio';

  const [email, setEmail] = useState(emailParam);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Selección de empresa (usuario multi-empresa): id en curso de canje.
  const [selectingId, setSelectingId] = useState<string | null>(null);

  // Biometría (SÓLO nativo): mostramos el botón si el dispositivo la soporta y
  // hay un refresh token guardado de un ingreso anterior.
  const [biometricReady, setBiometricReady] = useState(false);
  const [biometricBusy, setBiometricBusy] = useState(false);

  useEffect(() => {
    if (!IS_NATIVE) return;
    let cancelled = false;
    void (async () => {
      const available = await isBiometricAvailable();
      const hasToken = available ? await hasBiometricToken() : false;
      if (!cancelled) setBiometricReady(available && hasToken);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleBiometric = async () => {
    setError('');
    setBiometricBusy(true);
    try {
      const ok = await loginWithBiometrics();
      if (ok) {
        navigate(from, { replace: true });
      } else {
        // Token inválido/revocado o el usuario canceló: escondemos el atajo y
        // dejamos el login normal (sin ruido si simplemente canceló).
        setBiometricReady(await hasBiometricToken());
      }
    } finally {
      setBiometricBusy(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      // reCAPTCHA v3 (si está configurado): token para la acción de login.
      // En la app nativa NO ejecutamos reCAPTCHA (no corre en el WebView de
      // Capacitor y colgaría el botón); el backend saltea el captcha para clientes
      // nativos (header X-Native-Client, ver lib/api.ts + AuthController).
      const captchaToken = IS_NATIVE
        ? undefined
        : await executeRecaptcha(recaptchaSiteKey || undefined, 'login');
      const res = await login(email.trim(), password, captchaToken);
      // >1 empresa: NO navegamos; el AuthProvider dejó la selección pendiente y
      // esta pantalla renderiza el paso de elección de empresa.
      if (res.needsCompanySelection) return;
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'captcha_failed') {
        setError(t('auth.captcha.failed'));
      } else if (err instanceof ApiError && err.code === 'too_many_attempts') {
        setError(t('login.error.tooMany'));
      } else if (err instanceof ApiError && (err.status === 401 || err.code === 'invalid_credentials')) {
        setError(t('login.error.invalid'));
      } else {
        setError(t('common.genericError'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Selección de empresa (usuario con >1 empresa): canjea el select_token por la
  // sesión de la empresa elegida y navega al destino.
  const handleSelectCompany = async (companyId: string) => {
    setError('');
    setSelectingId(companyId);
    try {
      await selectCompany(companyId);
      navigate(from, { replace: true });
    } catch (err) {
      // Token de selección vencido (401/jwt) o empresa no válida (not_member):
      // volvemos al login con un mensaje claro.
      if (
        err instanceof ApiError &&
        (err.status === 401 || err.code === 'not_member' || err.code === 'token_expired')
      ) {
        cancelSelection();
        setError(t('company.select.expired'));
      } else {
        setError(t('common.genericError'));
      }
    } finally {
      setSelectingId(null);
    }
  };

  const handleCancelSelection = () => {
    setError('');
    cancelSelection();
  };

  return (
    // En nativo no hay landing ni panel de anuncios: login a una sola columna,
    // form centrado y grande. En web queda el layout de dos columnas con marca.
    <AuthLayout aside={IS_NATIVE ? undefined : <BrandPanel />}>
      <Seo title={t('seo.login.title')} description={t('seo.login.desc')} />
      {pendingCompanies ? (
        <CompanySelectStep
          companies={pendingCompanies}
          selectingId={selectingId}
          error={error}
          onSelect={handleSelectCompany}
          onBack={handleCancelSelection}
        />
      ) : (
      <div className="hero-rise relative overflow-hidden rounded-3xl border border-line bg-surface p-6 shadow-card sm:p-8">
        {/* Halo cálido interno para dar profundidad a la tarjeta (decorativo). */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-28"
          style={{
            background:
              'radial-gradient(80% 100% at 50% 0%, color-mix(in srgb, var(--brand) 11%, transparent), transparent 74%)',
          }}
        />

        <div className="relative">
          <div className="mb-7 space-y-1.5">
            <h1 className="font-display text-2xl font-extrabold tracking-tight text-ink sm:text-[1.75rem]">
              {t('login.title')}
            </h1>
            <p className="text-sm text-muted">{t('login.subtitle')}</p>
          </div>

          {/* Desbloqueo biométrico (sólo nativo, si hay token guardado). */}
          {biometricReady && (
            <>
              <Button
                type="button"
                fullWidth
                loading={biometricBusy}
                onClick={handleBiometric}
                className="h-11 font-display text-[15px] font-semibold shadow-card transition-all hover:-translate-y-0.5 hover:shadow-card-hover"
              >
                {!biometricBusy && <Fingerprint size={18} aria-hidden />}
                {biometricBusy ? t('auth.biometric.verifying') : t('auth.biometric.cta')}
              </Button>
              <OrDivider label={t('auth.biometric.or')} />
            </>
          )}

          {isInviteReason && !error && (
            <p className="mb-4 rounded-token border border-brand/30 bg-brand/10 px-4 py-3 text-sm text-ink">
              {t('login.invite.notice')}
            </p>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <FormError>{error}</FormError>

            <TextField
              label={t('common.email')}
              name="email"
              type="email"
              autoComplete="email"
              required
              placeholder={t('login.emailPlaceholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            <TextField
              label={t('common.password')}
              name="password"
              type="password"
              autoComplete="current-password"
              required
              placeholder={t('login.passwordPlaceholder')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />

            <Button
              type="submit"
              fullWidth
              loading={submitting}
              className="group mt-1 h-11 font-display text-[15px] font-semibold shadow-card transition-all hover:-translate-y-0.5 hover:shadow-card-hover"
            >
              {submitting ? (
                t('login.submitting')
              ) : (
                <>
                  {t('login.submit')}
                  <ArrowRight
                    size={17}
                    aria-hidden
                    className="transition-transform group-hover:translate-x-0.5"
                  />
                </>
              )}
            </Button>
          </form>

          <p className="mt-4 text-center text-sm">
            <Link
              to="/recuperar"
              className="font-medium text-brand underline-offset-2 transition-colors hover:text-brand-2 hover:underline"
            >
              {t('login.forgot')}
            </Link>
          </p>

          <p className="mt-3 text-center text-sm text-muted">
            {t('login.noAccount')}{' '}
            <Link
              to="/registro"
              onClick={() => trackCta('register')}
              className="font-semibold text-brand underline-offset-2 transition-colors hover:text-brand-2 hover:underline"
            >
              {t('login.createAccount')}
            </Link>
          </p>
        </div>
      </div>
      )}
    </AuthLayout>
  );
}

/** Separador "o" entre métodos de acceso: hairline + etiqueta mono centrada. */
function OrDivider({ label }: { label: string }) {
  return (
    <div className="my-5 flex items-center gap-3" aria-hidden>
      <span className="h-px flex-1 bg-line" />
      <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted">{label}</span>
      <span className="h-px flex-1 bg-line" />
    </div>
  );
}

/**
 * Panel de marca del login: logo lockup, descripción del sistema, centro de
 * anuncios público, y accesos a la landing + soporte por WhatsApp.
 * Fondo `surface` con halo de marca (tokens) para que se vea en claro y oscuro.
 */
function BrandPanel() {
  return (
    <div className="hero-rise relative flex h-full flex-col overflow-hidden rounded-3xl border border-line bg-surface p-6 shadow-card sm:p-8">
      {/* Profundidad de marca (decorativa, aria-hidden): halo cálido arriba +
          textura ñandutí muy sutil abajo-derecha. Colores desde tokens. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-44"
        style={{
          background:
            'radial-gradient(70% 100% at 50% 0%, color-mix(in srgb, var(--brand) 15%, transparent), transparent 70%)',
        }}
      />
      <div
        aria-hidden
        className="landing-nanduti pointer-events-none absolute -bottom-40 -right-40 h-80 w-80"
      />

      <div className="relative flex flex-1 flex-col">
        <img
          src={`${import.meta.env.BASE_URL}brand/logo-lockup.png`}
          alt="Tembiapo"
          className="h-9 w-auto self-start"
        />
        <h2 className="mt-6 max-w-[16ch] font-display text-xl font-extrabold leading-tight tracking-tight text-ink sm:text-2xl">
          {t('login.brand.title')}
        </h2>
        <p className="mt-2.5 max-w-[38ch] text-sm text-muted">{t('login.brand.tagline')}</p>

        <AnnouncementsCenter />

        <div className="mt-6 flex flex-col gap-2.5 border-t border-line pt-6 sm:flex-row">
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex h-11 flex-1 items-center justify-center gap-2 rounded-token bg-good px-4 font-display text-sm font-semibold text-white shadow-card transition-all hover:-translate-y-0.5 hover:shadow-card-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
          >
            <WhatsAppIcon size={18} />
            {t('whatsapp.support')}
          </a>
          <Link
            to="/"
            className="inline-flex h-11 flex-1 items-center justify-center gap-2 rounded-token border border-line bg-surface px-4 font-display text-sm font-semibold text-ink transition-all hover:-translate-y-0.5 hover:border-brand hover:text-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
          >
            <ArrowLeft size={16} />
            {t('login.brand.backHome')}
          </Link>
        </div>
      </div>
    </div>
  );
}

/** Lista de avisos activos (público). Con estados de carga / vacío. */
function AnnouncementsCenter() {
  const q = useAnnouncements();
  const items = q.data ?? [];

  return (
    <section className="mt-7 flex-1" aria-label={t('announcements.title')}>
      <h3 className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-brand-2">
        <span className="grid h-6 w-6 place-items-center rounded-lg bg-brand/10 text-brand">
          <Megaphone size={13} aria-hidden />
        </span>
        {t('announcements.title')}
      </h3>

      <div className="mt-3">
        {q.isLoading ? (
          <div className="grid place-items-center py-6">
            <Spinner className="h-5 w-5" />
          </div>
        ) : items.length === 0 ? (
          <p className="rounded-token border border-dashed border-line px-4 py-6 text-center text-sm text-muted">
            {t('announcements.empty')}
          </p>
        ) : (
          <ul className="max-h-64 space-y-2.5 overflow-y-auto pr-1">
            {items.map((a) => {
              // Si el visitante está en portugués y hay traducción, la mostramos;
              // si no, cae al español (base).
              const pt = getLocale() === 'pt';
              const title = pt && a.title_pt ? a.title_pt : a.title;
              const body = pt && a.body_pt ? a.body_pt : a.body;
              return (
                <li
                  key={a.id}
                  className="rounded-token border border-line bg-surface-2 px-3.5 py-3 transition-colors hover:border-brand-2/50"
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <h4 className="text-sm font-semibold text-ink">{title}</h4>
                    <time className="shrink-0 font-mono text-[11px] text-muted">
                      {formatRelative(a.created_at)}
                    </time>
                  </div>
                  <p className="mt-1 whitespace-pre-line text-sm text-muted">{body}</p>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
